/**
 * React hook for subscribing to a transcript document over the Polygraf
 * WebSocket. Thin adapter over {@link PolygrafConnection} — the transport
 * lifecycle (connect/reconnect/ping) lives there.
 */

import {
	buildAuthenticateMessage,
	type DocumentState,
	type DocumentUpdate,
	getNativeToken,
	PolygrafConnection,
	resolveWsUrl,
	type SessionFileInfo,
	type StreamingTextData,
	TranscriptFeed,
	WS_ERROR_ACCESS_DENIED,
} from '@polygraf/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSession } from '../lib/auth-client'

const PING_INTERVAL_MS = 15_000
const TYPING_IDLE_MS = 2000

interface UseTranscriptSocketOptions {
	documentId: string
	/** Set to false to disable the WebSocket connection (e.g. in local/iframe mode) */
	enabled?: boolean
	onUpdate?: (update: DocumentUpdate) => void
	onError?: (error: Error) => void
	onFileUploaded?: (file: SessionFileInfo) => void
	onFileRemoved?: () => void
}

interface UseTranscriptSocketReturn {
	document: DocumentState | null
	isConnected: boolean
	isConnecting: boolean
	hasWriter: boolean
	activeParagraphId: string | null
	isWriterTyping: boolean
	error: string | null
	/**
	 * True when the server refused the subscribe with `ACCESS_DENIED` (MP-215):
	 * the document sits in a space/room this viewer may not read. Terminal for
	 * this documentId — deliberately NOT surfaced through `error`, which drives
	 * retry/reconnect affordances that cannot help here.
	 */
	accessDenied: boolean
	/**
	 * `document` came from a snapshot or a live update since the socket last
	 * (re)subscribed. False while the socket is down or its subscribe awaits
	 * an answer: `document` may then be the copy from before the socket
	 * dropped, and the viewer keeps newer text it already shows (MU N3c).
	 */
	isSocketCurrent: boolean
	reconnect: () => void
}

export function useTranscriptSocket({
	documentId,
	enabled = true,
	onUpdate,
	onError,
	onFileUploaded,
	onFileRemoved,
}: UseTranscriptSocketOptions): UseTranscriptSocketReturn {
	// Kept with the documentId it belongs to: after a source switch the old
	// source's text must not be returned for the new one, not even for the
	// render before the connection effect has reset it (review, 7. 10. 2026).
	const [held, setHeld] = useState<{ documentId: string; document: DocumentState | null } | null>(
		null,
	)
	const document = held?.documentId === documentId ? held.document : null
	const [isConnected, setIsConnected] = useState(false)
	const [isConnecting, setIsConnecting] = useState(true)
	const [hasWriter, setHasWriter] = useState(false)
	const [activeParagraphId, setActiveParagraphId] = useState<string | null>(null)
	const [isWriterTyping, setIsWriterTyping] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [accessDenied, setAccessDenied] = useState(false)
	const [isSocketCurrent, setIsSocketCurrent] = useState(false)

	// MP-194: a logged-in viewer reports its name so the transcriber's roster
	// shows WHO is reading, not just a count. Anonymous viewers have no name and
	// stay unnamed. Presence telemetry only — never an auth signal.
	const { data: authSession } = useSession()
	const viewerName = authSession?.user?.name ?? null

	const connectionRef = useRef<PolygrafConnection | null>(null)
	/** What `document` holds, and for which documentId (seeds a re-created feed). */
	const heldRef = useRef<{ documentId: string; document: DocumentState | null } | null>(null)
	const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Callback refs prevent reconnects when consumer-supplied callbacks change.
	const onUpdateRef = useRef(onUpdate)
	const onErrorRef = useRef(onError)
	const onFileUploadedRef = useRef(onFileUploaded)
	const onFileRemovedRef = useRef(onFileRemoved)
	useEffect(() => {
		onUpdateRef.current = onUpdate
		onErrorRef.current = onError
		onFileUploadedRef.current = onFileUploaded
		onFileRemovedRef.current = onFileRemoved
	}, [onUpdate, onError, onFileUploaded, onFileRemoved])

	useEffect(() => {
		if (!enabled) {
			setIsConnecting(false)
			return
		}

		setIsConnecting(true)
		setError(null)
		setAccessDenied(false)

		// MU N3c: the document, and whether a subscribe awaits its answer, live
		// in a TranscriptFeed (@polygraf/shared, unit-tested) — React state only
		// mirrors what it holds.
		// Another source (the source selector keeps this page mounted): nothing
		// held belongs to it, and the previous source's text must not linger
		// under its name until its first answer.
		if (heldRef.current && heldRef.current.documentId !== documentId) {
			heldRef.current = null
			setHeld(null)
			setIsSocketCurrent(false)
			setHasWriter(false)
			setActiveParagraphId(null)
			setIsWriterTyping(false)
		}
		const feed = new TranscriptFeed(documentId, heldRef.current?.document ?? null)
		const show = (next: DocumentState | null) => {
			heldRef.current = { documentId, document: next }
			setHeld(heldRef.current)
		}
		const syncCurrent = () => setIsSocketCurrent(feed.isCurrent)
		const subscribe = () => {
			feed.subscribed()
			syncCurrent()
			conn.send({ type: 'subscribe', documentId })
		}

		const conn = new PolygrafConnection(
			{
				url: resolveWsUrl({
					envWsUrl: import.meta.env.VITE_WS_URL,
					envApiUrl: import.meta.env.VITE_API_URL,
				}),
				pingInterval: PING_INTERVAL_MS,
			},
			{
				onOpen: () => {
					setIsConnected(true)
					setIsConnecting(false)
					// A fresh connection re-evaluates access on the server (the viewer
					// may have signed in since the last denial) — drop the stale flag;
					// a still-denied subscribe re-raises it right away.
					setAccessDenied(false)
					// App WebView: no cookie on the socket, so identify in-band BEFORE
					// subscribing (the server gates subscribe on the connection's
					// identity). No-op outside a native WebView.
					const authenticate = buildAuthenticateMessage(getNativeToken())
					if (authenticate) conn.send(authenticate)
					subscribe()
				},
				onClose: () => {
					feed.disconnected()
					syncCurrent()
					setIsConnected(false)
					setIsConnecting(false)
				},
				onError: (err) => {
					setError(err.message)
					setIsConnecting(false)
					onErrorRef.current?.(err)
				},
			},
		)
		connectionRef.current = conn

		conn.on('document_state', (data) => {
			show(feed.snapshot(data.document as DocumentState))
			syncCurrent()
			setHasWriter(true)
		})

		conn.on('document_update', (data) => {
			const update = data as unknown as DocumentUpdate
			// MU N3c: the HTTP-writer bridge names the version each update was
			// diffed from, and no longer re-seeds every viewer when one joins. An
			// update this viewer already has is dropped; one it cannot apply means
			// it missed something, and it asks for a snapshot for itself alone.
			// Updates without a base version still apply as before, seeding a
			// document when none arrived yet (MP-182/186).
			const before = feed.document
			const result = feed.update(update)
			syncCurrent()
			// subscribe() also marks the held copy as not current until the answer.
			if (result.resubscribe) subscribe()
			if (result.document === before) return
			show(result.document)
			// MU N3b: the HTTP-writer bridge now sends tail deltas, so a viewer can
			// receive transcript content without a `document_state` in between.
			// Content is proof of a writer — without this the toolbar showed
			// "disconnected" while text kept arriving.
			setHasWriter(true)
			onUpdateRef.current?.(update)
		})

		conn.on('waiting_for_writer', () => {
			feed.answered()
			syncCurrent()
			setHasWriter(false)
		})

		conn.on('writer_connected', (data) => {
			setHasWriter(true)
			if (data.document) show(feed.snapshot(data.document as DocumentState))
			else feed.answered()
			syncCurrent()
		})

		conn.on('writer_disconnected', () => {
			setHasWriter(false)
		})

		conn.on('streaming_text', (data) => {
			// Seed a fresh document when none arrived yet (MP-182/186) so the
			// streamed text is rendered instead of silently dropped while the
			// "writer typing" indicator shows.
			show(feed.streamingText(data as unknown as StreamingTextData))
			syncCurrent()
			setActiveParagraphId(data.paragraphId as string)
			setIsWriterTyping(true)
			if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
			typingTimeoutRef.current = setTimeout(() => setIsWriterTyping(false), TYPING_IDLE_MS)
		})

		conn.on('error', (data) => {
			// A refused or failed subscribe is an answer too; a later gap may ask
			// again. Errors that answer something else (a failed authenticate) land
			// here as well: clearing the wait early only re-enables the gap
			// resubscribe, while waiting on a frame that never comes would leave the
			// viewer unable to resync.
			feed.answered()
			syncCurrent()
			// MP-215: an in-band subscribe denial. The socket stays open by design;
			// this is a terminal state for the documentId, not a retryable error —
			// route it to the dedicated flag instead of the reconnect-driving one.
			if (data.code === WS_ERROR_ACCESS_DENIED) {
				setAccessDenied(true)
				return
			}
			const message = typeof data.message === 'string' ? data.message : 'unknown error'
			setError(message)
			onErrorRef.current?.(new Error(message))
		})

		conn.on('file_uploaded', (data) => {
			onFileUploadedRef.current?.(data.file as SessionFileInfo)
		})

		conn.on('file_removed', () => {
			onFileRemovedRef.current?.()
		})

		return () => {
			conn.disconnect()
			connectionRef.current = null
			if (typingTimeoutRef.current) {
				clearTimeout(typingTimeoutRef.current)
				typingTimeoutRef.current = null
			}
		}
	}, [documentId, enabled])

	// MP-194: announce the viewer's display name to the server once connected,
	// and again whenever the auth session resolves (the session fetch races the
	// WS connect) or a reconnect re-flips `isConnected`. Deliberately a separate
	// message from `subscribe` — the server just stamps the name on the socket
	// without re-seeding the document, so it never disturbs the streamed
	// transcript. Anonymous viewers (no name) send nothing.
	useEffect(() => {
		if (isConnected && viewerName) {
			connectionRef.current?.send({ type: 'viewer_identify', viewerName })
		}
	}, [isConnected, viewerName])

	const reconnect = useCallback(() => {
		connectionRef.current?.reconnect()
	}, [])

	// Mobile WebView WS zombie recovery: iOS suspends the WebView when the
	// host app backgrounds. The WebSocket stays open at the JS layer but no
	// data flows; the browser's `onclose` may not fire until a long TCP
	// timeout. When the page becomes visible again we check the socket still
	// answers, and only replace it when it does not — fixes "stuck on waiting
	// for writer" after reopening the mobile app (MP-160).
	//
	// The check is what MU N3 (2026-09-04) asked for: this handler used to
	// reconnect on EVERY hidden→visible transition, so a plain tab switch tore
	// down a healthy socket, re-subscribed, and the server's full
	// `document_state` reply reloaded the whole transcript and jumped the view
	// to the end.
	useEffect(() => {
		if (!enabled) return
		// `document` here is the global `Document`, not the `document` state
		// above. SSR guard.
		const doc =
			typeof globalThis !== 'undefined'
				? (globalThis as { document?: Document }).document
				: undefined
		if (!doc) return
		const onVisibilityChange = () => {
			if (doc.visibilityState !== 'visible') return
			void connectionRef.current?.ensureAlive().catch((err) => {
				// The probe itself failing means the socket is unusable; a fresh
				// one is the only recovery, and it must not surface as an
				// unhandled rejection.
				setError(err instanceof Error ? err.message : String(err))
				connectionRef.current?.reconnect()
			})
		}
		doc.addEventListener('visibilitychange', onVisibilityChange)
		return () => doc.removeEventListener('visibilitychange', onVisibilityChange)
	}, [enabled])

	return {
		document,
		isConnected,
		isConnecting,
		hasWriter,
		activeParagraphId,
		isWriterTyping,
		error,
		accessDenied,
		isSocketCurrent,
		reconnect,
	}
}
